import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { useI18n } from "@/lib/i18n";
import { formatIDR, api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Trash, Minus, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

export function Cart() {
  const { items, update, remove, subtotal } = useCart();
  const { t } = useI18n();
  const shipping = items.length ? 25000 : 0;
  const total = subtotal + shipping;

  if (!items.length) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-4xl sm:text-5xl">{t("cart.title")}</h1>
        <p className="mt-6 text-[hsl(var(--sk-muted-fg))]">{t("cart.empty")}</p>
        <Button asChild className="mt-8 rounded-full bg-[hsl(var(--sk-primary))] hover:bg-[hsl(var(--sk-primary))]/90">
          <Link to="/shop" data-testid="cart-browse">{t("cart.browse")}</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-20 pb-24 grid lg:grid-cols-3 gap-12">
      <div className="lg:col-span-2">
        <h1 className="font-display text-5xl tracking-tighter mb-10">{t("cart.title")}</h1>
        <div className="space-y-6" data-testid="cart-items">
          {items.map((it) => (
            <div key={it.product_id} data-testid={`cart-item-${it.product_id}`}
              className="flex gap-6 border-b border-[hsl(var(--sk-border))] pb-6">
              <div className="w-24 h-24 rounded-lg overflow-hidden bg-[hsl(var(--sk-muted))] shrink-0">
                {it.image_url && <img src={it.image_url} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1">
                <p className="font-display text-xl">{it.name}</p>
                <p className="text-sm text-[hsl(var(--sk-muted-fg))]">{formatIDR(it.price)}</p>
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex items-center border border-[hsl(var(--sk-border))] rounded-full">
                    <button onClick={() => update(it.product_id, it.quantity - 1)} className="p-2" data-testid={`qty-minus-${it.product_id}`}><Minus size={12} /></button>
                    <span className="w-8 text-center text-sm">{it.quantity}</span>
                    <button onClick={() => update(it.product_id, it.quantity + 1)} className="p-2" data-testid={`qty-plus-${it.product_id}`}><Plus size={12} /></button>
                  </div>
                  <button onClick={() => remove(it.product_id)} data-testid={`remove-${it.product_id}`}
                    className="text-xs text-[hsl(var(--sk-muted-fg))] hover:text-red-600 transition-colors flex items-center gap-1">
                    <Trash size={14} /> {t("cart.remove")}
                  </button>
                </div>
              </div>
              <p className="font-mono-stat text-sm">{formatIDR(it.price * it.quantity)}</p>
            </div>
          ))}
        </div>
      </div>
      <aside className="bg-[hsl(var(--sk-secondary))] rounded-2xl p-8 h-fit">
        <div className="space-y-3">
          <div className="flex justify-between text-sm"><span>{t("cart.subtotal")}</span><span className="font-mono-stat">{formatIDR(subtotal)}</span></div>
          <div className="flex justify-between text-sm"><span>{t("cart.shipping")}</span><span className="font-mono-stat">{formatIDR(shipping)}</span></div>
          <div className="flex justify-between text-lg font-medium pt-3 border-t border-[hsl(var(--sk-border))]"><span>{t("cart.total")}</span><span className="font-mono-stat">{formatIDR(total)}</span></div>
        </div>
        <Button asChild size="lg" className="w-full mt-6 rounded-full bg-[hsl(var(--sk-primary))] hover:bg-[hsl(var(--sk-primary))]/90">
          <Link to="/checkout" data-testid="go-checkout">{t("cart.checkout")}</Link>
        </Button>
      </aside>
    </div>
  );
}

export function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { t } = useI18n();
  const nav = useNavigate();
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", shipping_address: "", city: "", postal_code: "", notes: "" });
  const [placedOrder, setPlacedOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const shipping = 25000;
  const total = subtotal + shipping;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity })) };
      const r = await api.post("/orders", payload);
      setPlacedOrder(r.data);
      clear();
      toast.success(t("checkout.order_placed"));
    } catch (err) {
      toast.error("Failed to place order");
    } finally { setSubmitting(false); }
  };

  const pay = async () => {
    const r = await api.post(`/orders/${placedOrder.id}/pay`);
    setPlacedOrder(r.data);
    toast.success(t("checkout.thank_you"));
  };

  if (placedOrder) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24">
        <p className="label-eyebrow text-[hsl(var(--sk-primary))]">{t("checkout.order_placed")}</p>
        <h1 className="mt-3 font-display text-5xl">{placedOrder.order_number}</h1>
        <div className="mt-8 bg-[hsl(var(--sk-secondary))] rounded-2xl p-8 space-y-3">
          <div className="flex justify-between text-sm"><span>{t("checkout.status")}</span><span className="uppercase tracking-wider" data-testid="order-status">{placedOrder.payment_status}</span></div>
          <div className="flex justify-between text-lg pt-3 border-t border-[hsl(var(--sk-border))]"><span>{t("cart.total")}</span><span className="font-mono-stat">{formatIDR(placedOrder.total)}</span></div>
        </div>
        <p className="mt-4 text-xs text-[hsl(var(--sk-muted-fg))]">{t("checkout.pay_note")}</p>
        {placedOrder.payment_status === "pending" ? (
          <Button onClick={pay} size="lg" data-testid="mock-pay-btn" className="mt-6 rounded-full bg-[hsl(var(--sk-primary))] hover:bg-[hsl(var(--sk-primary))]/90">
            {t("checkout.pay_now")}
          </Button>
        ) : (
          <Button asChild size="lg" className="mt-6 rounded-full">
            <Link to="/">Back to Home</Link>
          </Button>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="max-w-6xl mx-auto px-6 lg:px-10 pt-20 pb-24 grid lg:grid-cols-3 gap-12">
      <div className="lg:col-span-2">
        <h1 className="font-display text-5xl tracking-tighter mb-10">{t("checkout.title")}</h1>
        <div className="space-y-4">
          <Field label={t("checkout.name")} value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} testid="co-name" required />
          <div className="grid md:grid-cols-2 gap-4">
            <Field label={t("checkout.email")} type="email" value={form.customer_email} onChange={(v) => setForm({ ...form, customer_email: v })} testid="co-email" required />
            <Field label={t("checkout.phone")} value={form.customer_phone} onChange={(v) => setForm({ ...form, customer_phone: v })} testid="co-phone" required />
          </div>
          <Field label={t("checkout.address")} value={form.shipping_address} onChange={(v) => setForm({ ...form, shipping_address: v })} testid="co-addr" required />
          <div className="grid md:grid-cols-2 gap-4">
            <Field label={t("checkout.city")} value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="co-city" required />
            <Field label={t("checkout.postal")} value={form.postal_code} onChange={(v) => setForm({ ...form, postal_code: v })} testid="co-postal" required />
          </div>
          <div>
            <Label className="label-eyebrow text-[hsl(var(--sk-muted-fg))]">{t("checkout.notes")}</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2" data-testid="co-notes" />
          </div>
        </div>
      </div>
      <aside className="bg-[hsl(var(--sk-secondary))] rounded-2xl p-8 h-fit sticky top-24">
        <p className="label-eyebrow text-[hsl(var(--sk-muted-fg))] mb-4">Order Summary</p>
        <div className="space-y-2 text-sm">
          {items.map((it) => (
            <div key={it.product_id} className="flex justify-between"><span>{it.name} × {it.quantity}</span><span className="font-mono-stat">{formatIDR(it.price * it.quantity)}</span></div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-[hsl(var(--sk-border))] space-y-2 text-sm">
          <div className="flex justify-between"><span>{t("cart.subtotal")}</span><span className="font-mono-stat">{formatIDR(subtotal)}</span></div>
          <div className="flex justify-between"><span>{t("cart.shipping")}</span><span className="font-mono-stat">{formatIDR(shipping)}</span></div>
          <div className="flex justify-between text-lg pt-2 border-t border-[hsl(var(--sk-border))]"><span>{t("cart.total")}</span><span className="font-mono-stat">{formatIDR(total)}</span></div>
        </div>
        <Button type="submit" disabled={submitting || !items.length} size="lg" data-testid="place-order-btn"
          className="w-full mt-6 rounded-full bg-[hsl(var(--sk-primary))] hover:bg-[hsl(var(--sk-primary))]/90">
          {submitting ? "…" : t("checkout.pay")}
        </Button>
        <p className="mt-3 text-[10px] text-[hsl(var(--sk-muted-fg))]">{t("checkout.pay_note")}</p>
      </aside>
    </form>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <Label className="label-eyebrow text-[hsl(var(--sk-muted-fg))]">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="mt-2" data-testid={testid} />
    </div>
  );
}
